import type { EnvironmentId, ServerProvider } from "@cmmd-forge/contracts";

import { useCmmdRuntimeInventorySnapshot } from "../../cmmd/cmmdRuntimeInventoryResource";
import { getStoredLocalDeviceRuntimeId } from "../../deviceRegistryIdentity";
import { usePrimaryEnvironmentId } from "../../environments/primary";
import { useSavedEnvironmentRegistryStore } from "../../environments/runtime";
import { useSavedEnvironmentRuntimeStore } from "../../environments/runtime/savedEnvironmentRuntimeStore";
import { classifyEnvironmentId } from "../../environments/environmentIdClassification";
import { useServerConfig } from "../../rpc/serverState";
import { resolveNewThreadProviderInventoryView } from "../newThread/NewThreadLanding.providerInventory";
import { useDraftProviderInventory } from "../newThread/useDraftProviderInventory";

interface ProviderInventoryConfig {
  readonly providers: ReadonlyArray<ServerProvider>;
}

export function resolveDraftRouteProviderBootstrap(input: {
  readonly targetEnvironmentId: EnvironmentId | null;
  readonly primaryEnvironmentId: EnvironmentId | null;
  readonly primaryServerConfig: ProviderInventoryConfig | null;
  readonly targetEnvironmentServerConfig: ProviderInventoryConfig | null;
  readonly lastKnownProviders: ReadonlyArray<ServerProvider> | null;
  readonly localDeviceRuntimeId: string | null;
}) {
  return resolveNewThreadProviderInventoryView(input);
}

export function resolveDraftRouteRuntimeId(input: {
  readonly targetEnvironmentId: string | null;
  readonly savedRuntimeId: string | null | undefined;
}): string | null {
  const savedRuntimeId = input.savedRuntimeId?.trim();
  return savedRuntimeId || classifyEnvironmentId(input.targetEnvironmentId ?? "").legacyRuntimeId;
}

/**
 * Reads the same passive inventory used by `/new` for a restored draft.
 *
 * It deliberately has no runtime admission side effect. The inventory comes
 * from the control-plane replica or a saved Environment descriptor.
 */
export function useDraftRouteProviderBootstrap(input: {
  readonly routeKind: "server" | "draft";
  readonly draftEnvironmentId: EnvironmentId | undefined;
}) {
  const targetEnvironmentId =
    input.routeKind === "draft" ? (input.draftEnvironmentId ?? null) : null;
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const primaryServerConfig = useServerConfig();
  const targetEnvironment = useSavedEnvironmentRuntimeStore((store) =>
    targetEnvironmentId ? (store.byId[targetEnvironmentId] ?? null) : null,
  );
  const savedRuntimeId = useSavedEnvironmentRegistryStore((store) =>
    targetEnvironmentId ? (store.byId[targetEnvironmentId]?.runtimeId ?? null) : null,
  );
  const runtimeId = resolveDraftRouteRuntimeId({
    targetEnvironmentId,
    savedRuntimeId,
  });
  const runtimeInventory = useCmmdRuntimeInventorySnapshot();
  const { providerInventory, providerStatuses } = useDraftProviderInventory({
    targetEnvironmentId,
    primaryEnvironmentId,
    primaryServerConfig,
    targetEnvironmentServerConfig: targetEnvironment?.serverConfig ?? null,
    runtimes: runtimeInventory.inventory?.runtimes ?? [],
    localDeviceRuntimeId: getStoredLocalDeviceRuntimeId(),
    runtimeId,
  });

  return {
    draftRouteProviderStatuses: input.routeKind === "draft" ? providerStatuses : undefined,
    providerInventory,
  };
}
